import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  Township,
  TownshipCreateRequest,
  TownshipFull,
  TownshipListResponse,
  TownshipUpdateRequest,
} from '../models/township.models';

/**
 * Thin client for the backend townships API (prefix /api/v1/townships).
 * All endpoints require an admin or root account.
 */
@Injectable({ providedIn: 'root' })
export class TownshipService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = `${import.meta.env['NG_APP_API_URL'] ?? ''}/api/v1/townships`;

  /** GET /townships/ — `page` is 0-based; `filter` is a partial name match. */
  listTownships(page: number, limit: number, filter: string): Observable<TownshipListResponse> {
    let params = new HttpParams().set('page', page).set('limit', limit);
    const trimmed = filter.trim();
    if (trimmed) params = params.set('filter', trimmed);
    return this.http.get<TownshipListResponse>(`${this.apiBase}/`, { params });
  }

  getTownship(id: string): Observable<TownshipFull> {
    return this.http.get<TownshipFull>(`${this.apiBase}/${id}`);
  }

  createTownship(body: TownshipCreateRequest): Observable<TownshipFull> {
    return this.http.post<TownshipFull>(`${this.apiBase}/`, body);
  }

  updateTownship(id: string, body: TownshipUpdateRequest): Observable<TownshipFull> {
    return this.http.put<TownshipFull>(`${this.apiBase}/${id}`, body);
  }

  /** Soft-deletes the township and returns the (deleted) record. */
  deleteTownship(id: string): Observable<TownshipFull> {
    return this.http.delete<TownshipFull>(`${this.apiBase}/${id}`);
  }
}
